import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { t } from '../../lib/i18n';
import PDFPageViewer from './PDFPageViewer';

/**
 * Mode PDF pour la fiche de personnage.
 *
 * Charge pdf.js depuis le CDN, fetche le PDF en mémoire (ArrayBuffer via axios),
 * affiche chaque page via PDFPageViewer + overlay de champs, et auto-sauvegarde
 * les modifications via PATCH avec debounce (800ms) et optimistic locking.
 *
 * Props :
 *  - campaignId          : string
 *  - sheetId             : string
 *  - sheetVersion        : number   — version courante pour l'optimistic locking
 *  - pdfAssetId          : string   — template.pdf_asset_id
 *  - pdfOverlay          : object   — template.pdf_overlay { pages: [...] }
 *  - builderAnnotations  : array    — template.builder_annotations[]
 *  - formData            : object   — data_json partagé avec le mode Formulaire
 *  - onFormDataChange    : fn       — (newFormData) => void
 *  - onVersionUpdate     : fn       — (newVersion: number) => void
 *  - isActive            : bool     — false quand l'onglet est masqué → flush immédiat
 *  - isMJ                : bool
 *  - isReadOnly          : bool     — true quand on visualise la fiche d'un autre
 *  - language            : string
 */
export default function CharacterPDFMode({
  campaignId,
  sheetId,
  sheetVersion,
  pdfAssetId,
  pdfOverlay,
  builderAnnotations,
  formData,
  onFormDataChange,
  onVersionUpdate,
  isActive,
  isMJ,
  isReadOnly,
  language,
}) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [pdfjsReady,   setPdfjsReady]   = useState(!!window.pdfjsLib);
  const [pdfDoc,       setPdfDoc]       = useState(null);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [totalPages,   setTotalPages]   = useState(0);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [isSaving,     setIsSaving]     = useState(false);
  const [retryCount,   setRetryCount]   = useState(0); // pour forcer le rechargement du PDF

  // ── Refs pour debounce + version ──────────────────────────────────────────
  const debounceTimerRef   = useRef(null);
  const pendingDataRef     = useRef(null);
  const currentVersionRef  = useRef(sheetVersion);
  const isSavingRef        = useRef(false);

  // ── Sync version depuis le parent (ex : après une sauvegarde du mode Formulaire) ──
  useEffect(() => {
    currentVersionRef.current = sheetVersion;
  }, [sheetVersion]);

  // ── Chargement de pdf.js depuis le CDN ────────────────────────────────────
  useEffect(() => {
    if (window.pdfjsLib) {
      setPdfjsReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfjsReady(true);
    };
    script.onerror = () => {
      toast.error(t('pdf_mode_engine_error', language));
    };
    document.head.appendChild(script);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch du PDF en ArrayBuffer + chargement pdf.js ──────────────────────
  useEffect(() => {
    if (!pdfjsReady || !pdfAssetId) return;

    let cancelled = false;
    setPdfLoadError(false);

    // Détruire l'ancien document si existant
    setPdfDoc((prev) => {
      if (prev) prev.destroy();
      return null;
    });
    setCurrentPage(1);

    (async () => {
      try {
        // axios injecte automatiquement le Bearer token (défini dans AuthContext)
        const res = await axios.get(`/api/admin/system-assets/${pdfAssetId}/file`, {
          responseType: 'arraybuffer',
        });
        if (cancelled) return;

        const typedArray = new Uint8Array(res.data);
        const doc = await window.pdfjsLib.getDocument({ data: typedArray }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
      } catch (err) {
        if (!cancelled) {
          console.error('[CharacterPDFMode] PDF load error:', err);
          setPdfLoadError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfjsReady, pdfAssetId, retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Flush du debounce quand on quitte l'onglet ───────────────────────────
  useEffect(() => {
    if (!isActive && pendingDataRef.current && debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      executeSave(pendingDataRef.current);
    }
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup au démontage ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // ── Map permissions : fieldKey → annotation ───────────────────────────────
  const annMap = useMemo(
    () => Object.fromEntries((builderAnnotations || []).map((a) => [a.fieldKey, a])),
    [builderAnnotations]
  );

  // ── Champs de la page courante ────────────────────────────────────────────
  const pageFields = useMemo(() => {
    const spec = pdfOverlay?.pages?.find((p) => p.page === currentPage - 1);
    return spec?.fields || [];
  }, [pdfOverlay, currentPage]);

  // ── Save avec protection contre les saves concurrents ────────────────────
  async function executeSave(data) {
    if (isSavingRef.current) {
      // Un save est déjà en cours → reporter
      debounceTimerRef.current = setTimeout(() => executeSave(pendingDataRef.current), 400);
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const res = await axios.patch(
        `/api/campaigns/${campaignId}/character-sheets/${sheetId}`,
        { data, version: currentVersionRef.current }
      );
      // CRITIQUE : mettre à jour la ref immédiatement (avant re-render)
      currentVersionRef.current = res.data.version;
      onVersionUpdate(res.data.version);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(t('pdf_mode_version_conflict', language));
      } else {
        toast.error(t('error_generic', language));
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  // ── Handler de changement de champ (appelé par PDFFieldOverlay) ───────────
  const handleFieldChange = useCallback(
    (newFormData) => {
      onFormDataChange(newFormData); // mise à jour UI immédiate

      if (isReadOnly) return; // jamais de save en lecture seule

      pendingDataRef.current = newFormData;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        executeSave(pendingDataRef.current);
      }, 800);
    },
    [isReadOnly, onFormDataChange] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Retry chargement PDF ──────────────────────────────────────────────────
  const handleRetry = () => {
    setPdfLoadError(false);
    setRetryCount((c) => c + 1); // déclenche un re-run de l'effect de chargement
  };

  // ── États de chargement / erreur ──────────────────────────────────────────
  if (!pdfjsReady || (!pdfDoc && !pdfLoadError)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-sm">{t('pdf_mode_loading', language)}</span>
      </div>
    );
  }

  if (pdfLoadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <span className="text-sm">{t('pdf_mode_load_error', language)}</span>
        <Button variant="outline" size="sm" onClick={handleRetry}>
          {t('pdf_mode_retry', language)}
        </Button>
      </div>
    );
  }

  // ── Rendu principal ───────────────────────────────────────────────────────
  return (
    <div className="relative">
      {/* Indicateur de sauvegarde */}
      {isSaving && (
        <div className="absolute top-0 right-0 z-20 flex items-center gap-1.5 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded-bl-md border border-border">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('pdf_mode_saving', language)}
        </div>
      )}

      <PDFPageViewer
        pdfDoc={pdfDoc}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageFields={pageFields}
        annMap={annMap}
        formData={formData}
        onFieldChange={handleFieldChange}
        isMJ={isMJ}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}
